import { mount, type MountHandle } from '@personalizehq/renderer-web';
import type { ExperienceDocument, Viewport } from '@personalizehq/schemas/ir';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

// PREVIEW ON A PHONE, TODAY (B4.1).
//
// The QR used to encode `yourscheme://phq-preview?t=…` — a custom URL scheme handed to
// `PHQ.handleDeepLink()`. That is still the right destination, and it still does not exist:
// `handleDeepLink` is P5.3 and the host app is P5.4. So the camera found a scheme nothing on the
// phone had registered and said "no usable data found", which is exactly correct of it.
//
// This page is the destination that DOES exist. It is a public URL, so any phone's browser opens it,
// and it mounts THE PRODUCTION RENDERER — the same `renderer-web` the dashboard canvas runs, the
// same one the native ports are conformance-tested against. So the wrap points, the rects and the
// paint are the real ones, on real glass, at a real device width.
//
// It is NOT a substitute for in-app preview and it does not pretend to be: a browser cannot show you
// how the sheet feels under a thumb, and it is not running the Swift decoder. When P5.3 lands, the
// scheme QR comes back and this stays as the no-install path.
//
// PUBLIC ON PURPOSE — a sibling of `login`, never under `_authed`. THE TOKEN IS THE CREDENTIAL (128
// bits, 15 minutes, one experience), which is the same reason `GET /v1/preview/:token` takes no write
// key: an author must be able to preview against a colleague's phone without handing over the key.

export const Route = createFileRoute('/preview/$token')({ component: Preview });

/** `VITE_API_URL` is `http://localhost:8787` in dev — and on a phone, `localhost` IS THE PHONE. So
 *  when this page is being served from a LAN address, the API is on that same host: keep the
 *  configured port, borrow the hostname we were actually reached on. Deployed builds point at a real
 *  domain and this rewrite never fires. */
function apiBase(): string {
  const configured = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787';
  const here = window.location.hostname;
  try {
    const url = new URL(configured);
    const loopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (loopback && here !== 'localhost' && here !== '127.0.0.1') {
      url.hostname = here;
      return url.origin;
    }
  } catch {
    // A malformed VITE_API_URL is the author's to fix; fall through and let the fetch say so.
  }
  return configured;
}

interface PreviewPayload {
  name: string;
  flow: {
    theme: ExperienceDocument['theme'];
    steps: { id: string; surface: ExperienceDocument['surface']; root: ExperienceDocument['root'] }[];
  };
  assets: Record<string, string>;
}

function Preview(): React.JSX.Element {
  const { token } = Route.useParams();
  const [payload, setPayload] = useState<PreviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    fetch(`${apiBase()}/v1/preview/${token}`)
      .then(async (res) => {
        const body: unknown = await res.json();
        if (!res.ok) throw new Error((body as { error?: string }).error ?? 'This preview link has expired');
        setPayload(body as PreviewPayload);
      })
      .catch((err: Error) => setError(err.message));
  }, [token]);

  if (error) return <Centered>{error}</Centered>;
  if (!payload) return <Centered>Loading the draft…</Centered>;

  const current = payload.flow.steps[step];
  if (!current) return <Centered>This draft has no steps.</Centered>;

  const doc: ExperienceDocument = {
    schema_version: 'v1',
    surface: current.surface,
    theme: payload.flow.theme,
    root: current.root,
  };

  return (
    <Stage
      // Remount on step change: switching `surface` tears down the shadow root, and a stale handle
      // would paint into a node that no longer exists (the same law `artboard.tsx` obeys).
      key={current.id}
      doc={doc}
      assets={payload.assets}
      name={payload.name}
      step={step}
      steps={payload.flow.steps.length}
      onStep={setStep}
    />
  );
}

function Stage({
  doc,
  assets,
  name,
  step,
  steps,
  onStep,
}: {
  doc: ExperienceDocument;
  assets: Record<string, string>;
  name: string;
  step: number;
  steps: number;
  onStep: (index: number) => void;
}): React.JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<MountHandle | null>(null);
  const stepRef = useRef({ step, steps, onStep });
  useEffect(() => {
    stepRef.current = { step, steps, onStep };
  });

  useEffect(() => {
    // THE PHONE'S OWN VIEWPORT — not a 390×844 stand-in. The entire reason to walk to a device is to
    // see it at the width and the FONT SCALE the user actually has, so both are read off the browser
    // rather than assumed. (The dashboard canvas pins them, on purpose; here that would be a lie.)
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const viewport: Viewport = {
      w: window.innerWidth,
      h: window.innerHeight,
      color_scheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      font_scale: root / 16,
      direction: 'ltr',
    };

    const handle = mount(doc, {
      container: frameRef.current as HTMLElement,
      viewport,
      assets,
      // A preview is a thing you look at and then leave. Dismiss ends it rather than destroying the
      // canvas out from under the author, which is what the DASHBOARD has to avoid — the two hosts
      // want opposite things from the same closure.
      onDismiss: () => undefined,
      onAction: (action) => {
        const { step: at, steps: count, onStep: go } = stepRef.current;
        if (action.type === 'flow_next' && at + 1 < count) go(at + 1);
        else if (action.type === 'flow_back' && at > 0) go(at - 1);
      },
    });
    handleRef.current = handle;

    return () => {
      handle.destroy();
      handleRef.current = null;
    };
  }, [doc, assets]);

  return (
    <div className="fixed inset-0">
      <div ref={frameRef} className="size-full" />

      {/* The preview CHROME (`previewSchema.name` exists for exactly this): an author holding a phone
          must be able to tell WHICH draft they are looking at, and that it is a draft.
          ON TOP OF THE RENDERER, EXPLICITLY. The renderer's own host is `position: fixed; inset: 0`,
          so without a z-index this pill was in the DOM, with the right text, and painted UNDER the
          document — invisible on the very device it exists to orient. */}
      <div
        className="pointer-events-none fixed inset-x-0 top-0 flex justify-center p-2"
        style={{ zIndex: 2147483647 }}
      >
        <span className="rounded-full bg-black/70 px-3 py-1 text-[11px] font-medium text-white backdrop-blur">
          {name} · draft{steps > 1 ? ` · step ${step + 1}/${steps}` : ''}
        </span>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="grid h-dvh place-items-center p-6 text-center text-sm text-muted-foreground">{children}</div>
  );
}
